import React, { useState } from "react";
import {
  TextField,
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { dummyFirms } from "../../../../../dummyData";
import { FileText, RefreshCw, Users } from "lucide-react";
import dayjs from "dayjs";
import QuickDateRange from "../../Components/QuickDateRange";

const GSTReportForm = () => {
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [errors, setErrors] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState("lastWeek");
  const [isConsolidated, setIsConsolidated] = useState(false);

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!isConsolidated && !selectedFirm) {
      newErrors.firm = "Please select a firm";
      isValid = false;
    }

    if (!fromDate) {
      newErrors.fromDate = "Please select from date";
      isValid = false;
    }

    if (!toDate) {
      newErrors.toDate = "Please select to date";
      isValid = false;
    }

    if (fromDate && toDate && fromDate.isAfter(toDate)) {
      newErrors.dateRange = "From date cannot be after to date";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      // The navigation will be handled by NavLink
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setSelectedFirm(null);
    setFromDate(dayjs());
    setToDate(dayjs());
    setTimeRange("lastWeek");
    setIsConsolidated(false);
    setErrors({});
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleRangeChange = (range, fromDateValue, toDateValue) => {
    setTimeRange(range);

    if (fromDateValue && toDateValue) {
      setFromDate(fromDateValue);
      setToDate(toDateValue);

      // Clear any date-related errors when date range changes
      setErrors({
        ...errors,
        fromDate: undefined,
        toDate: undefined,
        dateRange: undefined,
      });
    }
  };

  const handleConsolidatedToggle = () => {
    setIsConsolidated(!isConsolidated);

    // Clear firm selection and related errors when switching to consolidated
    if (!isConsolidated) {
      setSelectedFirm(null);
      setErrors({
        ...errors,
        firm: undefined,
      });
    }
  };

  const allFirmsOption = {
    id: "all-firms",
    firmName: "All Firms",
    ownerName: "Consolidated Report",
    phoneNumber: "",
  };

  return (
    <div className="rounded-lg overflow-hidden">
      <div className="p-6">
        <div className="mb-6 flex items-center gap-2 text-gray-700">
          <FileText size={20} />
          <span className="font-medium">
            Please fill in the details to generate a GST report
          </span>
        </div>

        <form onSubmit={handleSubmit} className="max-w-4xl">
          <div className="bg-[#F9FAFB] p-4 rounded-lg mb-6 border border-gray-100">
            <div className="flex items-center gap-3">
              <Users size={20} className="text-[#5652B0]" />
              <FormControlLabel
                control={
                  <Switch
                    checked={isConsolidated}
                    onChange={handleConsolidatedToggle}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#5652B0",
                        "&:hover": {
                          backgroundColor: "rgba(86, 82, 176, 0.08)",
                        },
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#5652B0",
                        },
                    }}
                  />
                }
                label={
                  <span className="text-base font-medium text-gray-700">
                    Generate Consolidated GST Report (All Firms)
                  </span>
                }
              />
            </div>
            <p className="text-sm text-gray-500 ml-9 mt-1">
              {isConsolidated
                ? "This will generate a consolidated GST report for all firms within the selected date range"
                : "Select this option to generate a comprehensive GST report across all firms for filing returns"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
            <FormControl error={!!errors.firm} fullWidth>
              <Autocomplete
                id="firm-autocomplete"
                options={isConsolidated ? [allFirmsOption] : dummyFirms}
                getOptionLabel={option => option.firmName}
                disabled={isConsolidated}
                value={isConsolidated ? allFirmsOption : selectedFirm}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <div className="flex flex-col">
                      <span className="font-medium">{option.firmName}</span>
                      <span className="text-xs text-gray-500">
                        {option.ownerName}{" "}
                        {option.phoneNumber ? `| ${option.phoneNumber}` : ""}
                      </span>
                    </div>
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Firm Name"
                    variant="outlined"
                    size="small"
                    required={!isConsolidated}
                    error={!!errors.firm}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#5652B0",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#5652B0",
                      },
                    }}
                  />
                )}
                onChange={(_, newValue) => {
                  setSelectedFirm(newValue);
                  if (newValue) {
                    setErrors({ ...errors, firm: undefined });
                  }
                }}
              />
              {errors.firm && <FormHelperText>{errors.firm}</FormHelperText>}
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl error={!!errors.fromDate} fullWidth>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={newValue => {
                    setFromDate(newValue);
                    if (newValue) {
                      setErrors({
                        ...errors,
                        fromDate: undefined,
                        dateRange: undefined,
                      });
                    }
                    setTimeRange("custom"); // Switch to custom when date is manually changed
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      required: true,
                      error: !!errors.fromDate || !!errors.dateRange,
                      helperText:
                        errors.fromDate ||
                        (errors.dateRange && errors.fromDate
                          ? errors.dateRange
                          : ""),
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#5652B0",
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#5652B0",
                        },
                      },
                    },
                  }}
                />
              </FormControl>
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl error={!!errors.toDate} fullWidth>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={newValue => {
                    setToDate(newValue);
                    if (newValue) {
                      setErrors({
                        ...errors,
                        toDate: undefined,
                        dateRange: undefined,
                      });
                    }
                    setTimeRange("custom"); // Switch to custom when date is manually changed
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      required: true,
                      error: !!errors.toDate || !!errors.dateRange,
                      helperText:
                        errors.toDate ||
                        (errors.dateRange && !errors.fromDate
                          ? errors.dateRange
                          : ""),
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#5652B0",
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#5652B0",
                        },
                      },
                    },
                  }}
                />
              </FormControl>
            </LocalizationProvider>
          </div>

          <QuickDateRange
            selectedRange={timeRange}
            onRangeChange={handleRangeChange}
            showFinancialYears={true}
          />

          <div className="mt-8 flex gap-4">
            <NavLink
              to={`/report/gstreporttable${
                isConsolidated ? "?consolidated=true" : ""
              }`}>
              <button
                type="submit"
                disabled={!isConsolidated && !selectedFirm}
                className={`px-6 py-2.5 rounded-lg text-base font-medium text-white 
                  ${
                    !isConsolidated && !selectedFirm
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#5652B0] hover:bg-[#4A47A3] cursor-pointer"
                  } 
                  transition-all`}>
                Generate GST Report
              </button>
            </NavLink>

            <button
              type="button"
              onClick={handleRefresh}
              className="px-6 py-2.5 rounded-lg text-base font-medium border-2 border-[#5652B0] text-[#5652B0] hover:bg-[#5652B0] hover:text-white transition-all flex items-center gap-2">
              <RefreshCw
                size={16}
                className={isRefreshing ? "animate-spin" : ""}
              />
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GSTReportForm;
